/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_BUTTON_USAGE_EXCHANGE_RATES
 * Date:         2026-01-07
 * Modified By:  Cursor
 *
 * Description:
 * Replaces the hero call-to-action buttons with the new AdminButton wrapper
 * to lock the exchange rates view into the approved design-system tokens.
 *
 * File Size Check:
 * - Current: 243
 * - Adding: ~12
 * - Projected: ~255
 * - Decision: Proceed
 *
 * Typography Compliance (UI files only):
 * - Utilities used: .u-P2-16-medium (via btn-black / btn-figma-1)
 * - Tokens used: --font-size-body-small, --line-height-body-small, --font-family
 *
 * Related Files:
 * - client/src/components/admin/AdminButton.tsx
 * - client/src/features/admin/users/AdminWorkingDataBox.tsx
 * - client/src/features/admin/scheduler/AdminExchangeRatesPage.tsx
 */

/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    MIGRATE_EXCHANGE_RATES_TO_SCHEDULER_UI
 * Date:         2026-01-05
 * Modified By:  Cursor
 *
 * Description:
 * Merge the full exchange rates admin UI (filters, table, export, refresh)
 * into the scheduler exchange-rates detail page so the feature is accessible
 * at `/admin/scheduler/exchange-rates`. Keeps the status hero card and uses
 * existing `listFx`/`refreshFx` services for data and refresh functionality.
 *
 * File Size Check:
 * - Current: ~180 lines
 * - Adding: ~0 lines
 * - Projected: ~180 lines
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/admin/exchange/FxFilters.tsx
 * - client/src/features/admin/exchange/FxTable.tsx
 * - client/src/features/admin/exchange/useFxList.ts
 * - client/src/services/fx.ts
 * - client/src/services/schedulerApi.ts
 */

import React, { useState, useEffect } from "react";
import { Alert, Spinner, Card, Row, Col } from "react-bootstrap";
import schedulerApi from "../../../services/schedulerApi";
import { listFx } from "../../../services/fx";
import FxFilters from "../exchange/FxFilters";
import FxTable from "../exchange/FxTable";
import { useFxList } from "../exchange/useFxList";
import AdminButton from "../../../components/admin/AdminButton";
import "./AdminExchangeRatesPage.scss";

interface DashboardData {
  exchangeRates: {
    status: string;
    lastUpdateTimestamp: string | null;
    nextUpdateTimestamp: string | null;
    nextUpdateFrequency: string;
    currenciesActive: number;
  };
}

const AdminExchangeRatesPage: React.FC = () => {
  // Status/dashboard data (hero card)
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);

  // Update trigger state
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  // Reuse fx listing hook for full table behavior
  const { query, data, loading, fetchList } = useFxList();

  // Load dashboard payload (status card)
  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      setDashboardLoading(true);
      const response = await schedulerApi.fetchSchedulerDashboard();
      if (response && response.success && response.data) {
        setDashboard(response.data);
      }
    } catch (err: any) {
      console.error("Failed to load dashboard:", err);
    } finally {
      setDashboardLoading(false);
    }
  };

  const handleUpdateRates = async () => {
    try {
      setUpdating(true);
      setMessage(null);
      await schedulerApi.triggerExchangeRates({});
      setMessage({ type: "success", text: "Exchange rate update job scheduled successfully." });
      // Refresh dashboard and table shortly after scheduling
      setTimeout(() => {
        loadDashboard();
        fetchList({ page: 1 });
      }, 3000);
    } catch (err: any) {
      console.error("Trigger exchange rates failed:", err);
      setMessage({ type: "error", text: err?.message || "Failed to trigger update" });
    } finally {
      setUpdating(false);
    }
  };

  // Export CSV by paginating through listFx
  const handleExport = async () => {
    try {
      const pageSize = 200;
      let p = 1;
      let all: any[] = [];
      while (true) {
        const { data: pageData } = await listFx({ ...query, page: p, limit: pageSize });
        if (pageData && pageData.success) {
          all = all.concat(pageData.data.rows || []);
          const totalPages = Math.ceil((pageData.data.total || 0) / (pageData.data.limit || pageSize)) || 1;
          if (p >= totalPages) break;
          p++;
        } else {
          throw new Error("Failed to fetch data for export");
        }
      }

      if (!all.length) {
        setMessage({ type: "error", text: "No results to export." });
        return;
      }

      const header = ["base", "quote", "rate", "updatedAt", "source"];
      const csv = [header.join(","), ...all.map((r: any) => [r.base, r.quote, r.rate, r.updatedAt || "", r.source || ""].join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fx-export-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error("Export error:", err);
      setMessage({ type: "error", text: err?.message || "Failed to export data" });
    }
  };

  const formatDate = (dateString: string | null, fallback = "N/A") => {
    if (!dateString) return fallback;
    return new Date(dateString).toLocaleString();
  };

  const formatStatus = (status?: string) => {
    if (!status) return "Status: N/A";
    if (status === "active") return "Status: 🟢 Active";
    return `Status: ${status}`;
  };

  return (
    <div className="admin-exchange-rates-page container-fluid py-3">
      <h2>Exchange Rates</h2>
      <p className="text-muted mb-4">Daily currency rate updates for platform trading</p>

      {message && (
        <Alert variant={message.type === "success" ? "success" : "danger"} dismissible onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card className="mb-4">
        <Card.Body>
          {dashboardLoading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : dashboard ? (
            <>
              <Row className="mb-3">
                <Col>
                  <div className="mb-2">
                    <strong>{formatStatus(dashboard.exchangeRates.status)}</strong>
                  </div>
                  <div className="mb-2">
                    <strong>Last Update:</strong> {formatDate(dashboard.exchangeRates.lastUpdateTimestamp)} · Success
                  </div>
                  <div className="mb-2">
                    <strong>Next Update:</strong> {formatDate(dashboard.exchangeRates.nextUpdateTimestamp)} ·{" "}
                    {dashboard.exchangeRates.nextUpdateFrequency || "Daily"}
                  </div>
                  <div>
                    <strong>Currencies:</strong> {dashboard.exchangeRates.currenciesActive} active
                  </div>
                </Col>
              </Row>
              {/* [2026-01-07] Buttons: use AdminButton so the hero CTAs honor the design system. */}
              <div className="d-flex gap-2 flex-wrap">
                <AdminButton intent="primary" onClick={handleUpdateRates} disabled={updating}>
                  {updating ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    "Update Now"
                  )}
                </AdminButton>
                <AdminButton intent="secondary" onClick={handleExport}>
                  Export CSV
                </AdminButton>
              </div>
            </>
          ) : (
            <Alert variant="warning">Unable to load exchange rates status.</Alert>
          )}
        </Card.Body>
      </Card>

      {/* Filters + Table */}
      <div className="mb-3">
        <FxFilters
          initial={{
            q: query.q,
            base: query.base,
            quote: query.quote,
            minRate: query.minRate,
            maxRate: query.maxRate,
          }}
          onApply={(vals) => fetchList({ ...vals, page: 1 })}
        />
      </div>

      <div>
        {loading && (
          <div className="text-center py-3">
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            Loading exchange rates...
          </div>
        )}

        {!loading && (
          <FxTable
            rows={data?.items || []}
            page={data?.page || query.page || 1}
            pages={data?.pages || 1}
            sortBy={query.sortBy}
            sortOrder={query.sortOrder}
            onSort={(field) => {
              const nextOrder = query.sortBy === field && query.sortOrder === "asc" ? "desc" : "asc";
              fetchList({ sortBy: field, sortOrder: nextOrder, page: 1 });
            }}
            onPage={(p) => fetchList({ page: p })}
          />
        )}
      </div>
    </div>
  );
};

export default AdminExchangeRatesPage;
